import { BULK_SETTLEMENT_DEFAULT_COMMENT } from '../constants';
import type {
  BulkSettlementTimeValue,
  BulkSettlementTransactionCardItem,
} from '../types';

const buildSaleTime = (businessDate: string, time: BulkSettlementTimeValue): string => {
  const [year = '', month = '', day = ''] = businessDate.split('-');
  const hour12 = Number(time.hour);
  const normalizedHour = Number.isFinite(hour12) ? hour12 % 12 : 0;
  const hour24 = time.meridiem === 'PM' ? normalizedHour + 12 : normalizedHour;
  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    time.meridiem === 'AM' && normalizedHour === 12 ? 0 : hour24,
    Number(time.minute),
    0,
    0,
  );
  const timezoneOffsetMinutes = -date.getTimezoneOffset();
  const sign = timezoneOffsetMinutes >= 0 ? '+' : '-';
  const absoluteOffsetMinutes = Math.abs(timezoneOffsetMinutes);
  const offsetHours = String(Math.floor(absoluteOffsetMinutes / 60)).padStart(2, '0');
  const offsetMinutes = String(absoluteOffsetMinutes % 60).padStart(2, '0');

  return `${businessDate}T${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes(),
  ).padStart(2, '0')}:00${sign}${offsetHours}:${offsetMinutes}`;
};

export const buildBulkSettlementTransactionsPayload = ({
  businessDate,
  cards,
  tableId,
}: {
  businessDate: string;
  cards: BulkSettlementTransactionCardItem[];
  tableId: string;
}) => {
  return {
    business_date: businessDate,
    table_id: tableId,
    transactions: cards.map(card => ({
      operation_id: card.id,
      sale_time: buildSaleTime(businessDate, card.time),
      cashier_id: card.cashierId,
      payment_method: card.paymentMethod as 'cash' | 'card' | 'other',
      payment_method_id: card.paymentMethodId || null,
      method_name: card.methodName || null,
      number_of_persons: Number(card.peopleCount) || 0,
      received_amount: Number(card.total) || 0,
      comment: BULK_SETTLEMENT_DEFAULT_COMMENT,
      item_type: card.itemType as 'menu_item' | 'bulk_item',
      ordered_items:
        card.itemType === 'menu_item'
          ? card.selectedItems.map(item => ({
              menu_item_id: item.itemId,
              quantity: item.quantity,
              // Both halves of the price key, and no amount: the server resolves
              // each component's premium from the (group, item) pair and never
              // trusts a price off the wire. Omitted entirely for a plain item.
              ...(item.addons?.length
                ? {
                    addons: item.addons.map(addon => ({
                      option_id: addon.addonId,
                      item_id: addon.itemId,
                    })),
                  }
                : {}),
            }))
          : [],
      bulk_item:
        card.itemType === 'bulk_item'
          ? { name: 'Bulk Item', total: Number(card.total) || 0 }
          : null,
    })),
  };
};
